import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Smartphone, Share2, Copy, CheckCircle2, Apple, ArrowRight, Wifi, Trophy, Bell } from "lucide-react";
import { API } from "@/lib/api";
import { toast } from "sonner";

const IOS_UA = /iPad|iPhone|iPod/;
const ANDROID_UA = /Android/;

function detectPlatform() {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  if (IOS_UA.test(ua) && !window.MSStream) return "ios";
  if (ANDROID_UA.test(ua)) return "android";
  return "desktop";
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function Install() {
  const [platform, setPlatform] = useState("desktop");
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/install`;
  }, []);

  useEffect(() => {
    setPlatform(detectPlatform());
    if (isStandalone()) setInstalled(true);

    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) {
      toast.info("Ouvre ce lien dans Chrome puis appuie de nouveau", { duration: 4000 });
      return;
    }
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setDeferred(null);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Lien copié");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Impossible de copier — sélectionne le lien à la main");
    }
  };

  const nativeShare = async () => {
    const data = {
      title: "ASS les Régionnaires",
      text: "Installe l'appli du club :",
      url: shareUrl,
    };
    if (navigator.share) {
      try {
        await navigator.share(data);
      } catch {}
    } else {
      copyLink();
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] text-white">
      {/* Hero */}
      <div className="max-w-3xl mx-auto px-5 pt-10 pb-8">
        <div className="flex items-center gap-3 mb-8">
          <img
            src="/icon-192.png"
            alt="ASS les Régionnaires"
            className="w-14 h-14 rounded-xl"
          />
          <div>
            <div className="text-[11px] font-mono-stat uppercase tracking-[0.25em] text-[#E5B85C]">
              Association Sportive
            </div>
            <div className="font-display font-black text-lg uppercase leading-tight">
              Les Régionnaires
            </div>
          </div>
        </div>

        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight mb-4">
          Installe l'app du club
        </h1>
        <p className="text-slate-400 text-lg leading-relaxed max-w-xl">
          Gratuit, sans passer par le store. Ton téléphone ajoute une vraie icône
          sur l'écran d'accueil, comme une app native.
        </p>

        {/* Perks */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-8">
          <Perk icon={Bell} title="Notifs match" desc="Recevoir les rappels 24h avant chaque match" />
          <Perk icon={Trophy} title="Stats en direct" desc="Buts, passes, cartons — tout est là" />
          <Perk icon={Wifi} title="Hors-ligne" desc="L'app s'ouvre même sans réseau (partiel)" />
        </div>

        {/* Install block */}
        <div
          data-testid="install-hero"
          className="mt-10 rounded-2xl bg-slate-900 border border-white/10 p-6 sm:p-8"
        >
          {installed ? (
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-400" />
              </div>
              <div className="flex-1">
                <div className="font-display font-bold text-lg uppercase">
                  App installée
                </div>
                <div className="text-sm text-slate-400">
                  Ouvre-la depuis ton écran d'accueil.
                </div>
              </div>
              <Link
                to="/login"
                data-testid="install-open-app"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] text-sm font-bold uppercase tracking-wide"
              >
                Ouvrir <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : platform === "ios" ? (
            <IOSSteps />
          ) : (
            <AndroidBlock deferred={deferred} onInstall={install} />
          )}
        </div>

        {/* Share strip */}
        <div className="mt-6 rounded-2xl bg-slate-900 border border-white/10 p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-3">
            <Share2 className="w-4 h-4 text-[#E5B85C]" />
            <div className="text-[11px] font-mono-stat uppercase tracking-widest text-[#E5B85C]">
              Partage à tes coéquipiers
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <img
              data-testid="install-qr"
              src={`${API}/install-qr.png`}
              alt="QR code"
              className="w-32 h-32 rounded-xl bg-slate-800 p-2 shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-400 mb-2">
                Scanne le QR ou copie le lien — chacun installe l'app en 30&nbsp;s.
              </div>
              <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-800 border border-white/5 mb-3">
                <span data-testid="install-share-url" className="flex-1 min-w-0 truncate text-sm font-mono">
                  {shareUrl}
                </span>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  data-testid="install-copy-link"
                  onClick={copyLink}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase tracking-wide"
                >
                  {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Copié" : "Copier le lien"}
                </button>
                <button
                  data-testid="install-native-share"
                  onClick={nativeShare}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] text-xs font-bold uppercase tracking-wide"
                >
                  <Share2 className="w-3.5 h-3.5" /> Partager
                </button>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(
                    "Installe l'appli ASS les Régionnaires : " + shareUrl,
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="install-whatsapp"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold uppercase tracking-wide"
                >
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center text-sm text-slate-500">
          Déjà un compte ?{" "}
          <Link to="/login" className="text-[#E5B85C] hover:underline font-bold">
            Se connecter →
          </Link>
        </div>
      </div>
    </div>
  );
}

function Perk({ icon: Icon, title, desc }) {
  return (
    <div className="p-4 rounded-xl bg-slate-900 border border-white/10">
      <Icon className="w-5 h-5 text-[#E5B85C] mb-2" />
      <div className="font-bold text-sm">{title}</div>
      <div className="text-xs text-slate-400 mt-1 leading-relaxed">{desc}</div>
    </div>
  );
}

function AndroidBlock({ deferred, onInstall }) {
  return (
    <div className="flex items-start gap-4 flex-wrap">
      <div className="w-14 h-14 rounded-lg bg-[#E5B85C]/15 flex items-center justify-center shrink-0">
        <Smartphone className="w-7 h-7 text-[#E5B85C]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-xl uppercase mb-1">
          Installation en 1 clic
        </div>
        <div className="text-sm text-slate-400 mb-4">
          {deferred
            ? "Appuie sur le bouton ci-dessous pour ajouter l'app sur ton écran d'accueil."
            : "Ouvre cette page dans Google Chrome puis reviens ici — un bouton apparaîtra."}
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="install-android-cta"
            onClick={onInstall}
            disabled={!deferred}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#E5B85C] hover:bg-[#D4A24E] disabled:opacity-50 disabled:cursor-not-allowed text-[#0B0F17] text-sm font-bold uppercase tracking-wide"
          >
            <Download className="w-4 h-4" /> Installer l'app
          </button>
          <ManualSteps />
        </div>
      </div>
    </div>
  );
}

function ManualSteps() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-bold uppercase tracking-wide"
      >
        Étape par étape
      </button>
      {open && (
        <ol className="mt-3 w-full space-y-2 text-sm text-slate-300">
          <Step n={1} text="Ouvre cette page dans Google Chrome" />
          <Step n={2} text="Appuie sur le menu ⋮ (en haut à droite)" />
          <Step n={3} text="Choisis “Installer l'application” ou “Ajouter à l'écran d'accueil”" />
          <Step n={4} text="Confirme — l'app apparaît sur ton écran" />
        </ol>
      )}
    </>
  );
}

function IOSSteps() {
  return (
    <div className="flex items-start gap-4 flex-wrap">
      <div className="w-14 h-14 rounded-lg bg-[#E5B85C]/15 flex items-center justify-center shrink-0">
        <Apple className="w-7 h-7 text-[#E5B85C]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-xl uppercase mb-1">
          Installation sur iPhone
        </div>
        <div className="text-sm text-slate-400 mb-4">
          Ouvre cette page dans <b className="text-white">Safari</b> puis suis
          les 3 étapes :
        </div>
        <ol className="space-y-2 text-sm text-slate-300">
          <Step n={1} text="Appuie sur l'icône Partager 📤 en bas de Safari" />
          <Step n={2} text="Fais défiler et choisis “Sur l'écran d'accueil”" />
          <Step n={3} text="Confirme avec “Ajouter” — l'app apparaît" />
        </ol>
      </div>
    </div>
  );
}

function Step({ n, text }) {
  return (
    <li className="flex items-start gap-3">
      <span className="w-6 h-6 shrink-0 rounded-full bg-[#E5B85C] text-[#0B0F17] flex items-center justify-center text-xs font-black">
        {n}
      </span>
      <span>{text}</span>
    </li>
  );
}
